import React, { useEffect } from "react";
import styles from "./NoAccessComponents.module.scss";
import { ReactComponent as ForbiddenIcon } from "./forbidden.svg";
import { ReactComponent as AccessDenied } from "./AccessDenied.svg";
import externals from "./../../../utils/externals";
import { TabHeader, Button, Card, Layout, Heading, Text,ExternalLink,Span } from "@netapp/bxp-design-system-react";
import { ConnectorIcon } from "@netapp/bxp-style/react-icons/Storage";
import { useLocation } from "react-router";
import { useSelector, useDispatch } from "react-redux";
import { ReactComponent as ComingSoonIcon } from "./coming-soon.svg";
import { ReactComponent as UpgradeRequiredIcon } from "./upgrade-required.svg";
import { isNewTenancyEnabled } from "utils/localstorage";

const AccessDeniedCardContent = ({headingText, primaryText, secondaryText}) => {
    return <>
        <Heading level={20} style={{marginTop:16, textAlign: "center"}} bold>{headingText}</Heading>
        <Text style={{marginTop:16}}>{primaryText}</Text>
        {secondaryText && <Text>{secondaryText}</Text>}
    </>
}

export const Forbidden = () => {
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden}/>
        <Heading>Contact the administrator of this system to gain access</Heading>
        <Text>Before you can use this connector, the administrator must add your user to the BlueXP Project.</Text>
        <ExternalLink trackCategory="NoAccessPages" href={externals.saasFrontend} newTab large>Go to BlueXP</ExternalLink>
    </section>
};

export const SaaSDisabled = () => {
    const isTenancyV4 = isNewTenancyEnabled();
    return <section className={styles.container} style={{ paddingTop: 152 }}>
        <ForbiddenIcon className={styles.forbidden}/>
        <Heading color="text-title">Your {isTenancyV4 ? 'organization' : 'account'} does not have access to this interface.</Heading>
        <Text>Your BlueXP Project has been disabled for use with the SaaS interface.</Text>
        <Text>If you have any questions, reach out to us by using the chat feature below.</Text>
    </section>
};

export const ServiceNeedsSaaSIsDisabled = ({ needHeader, currentService }) => {
    const { logo, name } = currentService || {};
    return <>
        {currentService && needHeader ? <TabHeader logo={logo} label={name}/> : ''}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <AccessDenied/>
                        <AccessDeniedCardContent headingText={"This service isn't supported when SaaS is disabled on your account."} primaryText={"Your BlueXP account has been disabled for use with the SaaS interface."} secondaryText={"If you have any questions, reach out to us by using the chat feature below."}/>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};

export const ServiceNeedsThirdPartyIsDisabled = ({ needHeader, currentService }) => {
    const { logo, name } = currentService || {};
    return <>
        {currentService && needHeader ? <TabHeader logo={logo} label={name}/> : ''}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <AccessDenied/>
                        <AccessDeniedCardContent headingText={"This is a third party service, it requires third-party services to be enabled in your account."} primaryText={"Third party services are disabled in your account."} secondaryText={"If you have any questions, reach out to us by using the chat feature below."}/>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};

export const NoContainerMessage = ({ needHeader, currentService: { logo, name } }) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={name}/>}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <ConnectorIcon color='text' style={{marginTop:6, width: 40, height: 28}}/>
                        <AccessDeniedCardContent headingText={"Something went wrong"} primaryText={"BlueXP is preparing the Connector. Please wait a few moments."}/>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};

export const PullingContainerMessage = ({ needHeader, currentService: { logo, name } }) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={name}/>}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <ConnectorIcon color='text' style={{marginTop:6, width: 40, height: 28}}/>
                        <Heading level={2} style={{marginTop:14}}>Something went wrong</Heading>
                        <Text style={{marginTop:16}}>BlueXP is preparing the Connector. Please wait a few moments.</Text>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};

export const UpgradeRequired = ({ needHeader, currentService: { logo, name } }) => {
    const dispatch = useDispatch();
    const provider = useSelector(state => state.tenancy.selectedAgent?.provider)

    let type = "instance type"

    if (provider === "gcp") {
        type = "machine type";
    } else if (provider === "azure") {
        type = "VM type";
    } else if (provider === "on-prem") {
        type = "VM"
    }

    return <>
        {needHeader && <TabHeader logo={logo} label={name}/>}
        <section className={styles.container} style={{ paddingTop: 60 }}>
            <UpgradeRequiredIcon style={{ marginBottom: 13 }}/>
            <Heading color="error" bold><Span style={{ textTransform: "capitalize" }}>{type}</Span> change is required.</Heading>
            <Text style={{ maxWidth: 485, textAlign: "center" }}>You need to change the {type} for your Connector so you can enjoy new capabilities that you’re currently missing.</Text>
            <Button style={{ width: 152, marginTop: 32 }} large onClick={() => {
                dispatch({
                    type: "CURRENT-CONNECTOR:OPEN-WEAK-CONNECTOR-DIALOG",
                    track: "from-no-access-page"
                })
            }}>Learn More</Button>
        </section>
    </>
};

export const NoLocalMessage = ({ needHeader, currentService }) => {
    const location = useLocation();
    const selectedAccountId = useSelector(state => state.tenancy.selectedAccountId);
    const connectorId = useSelector(state => state.tenancy.selectedAgentId);
    const connectorIdSuffix = connectorId ? `&connectorId=${connectorId}` : "";
    const url = `${externals.saasFrontend}${location.pathname}?accountId=${selectedAccountId}${connectorIdSuffix}`;
    const { logo, name } = currentService || {};

    return <>
        {currentService && needHeader ? <TabHeader logo={logo} label={name}/> : ''}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <AccessDenied/>
                        <Heading level={20} style={{marginTop:16, textAlign: "center"}} bold>This service is only accessible from BlueXP.</Heading>
                        <Text style={{marginTop:16, marginBottom:16}}>Visit the BlueXP UI to use this service.</Text>
                        <ExternalLink
                            large
                            href={url}
                            newTab
                        > Go to BlueXP</ExternalLink>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};

export const ComingSoonMessage = ({ needHeader, currentService: { logo, name }}) => {
    return <>
        {needHeader && <TabHeader logo={logo} label={name}/>}
        <section className={styles.container}>
            <ComingSoonIcon className={styles['coming-soon']}/>
            <Heading>We can’t access this service right now.</Heading>
            <Text>This service is not yet supported with the cloud provider where the Connector is deployed.</Text>
        </section>
    </>
};

export const NoPermissionMessage = ({ needHeader, currentService }) => {
    const { logo, name } = currentService || {};
    const isTenancyV4 = isNewTenancyEnabled();

    return <>
        {currentService && needHeader ? <TabHeader logo={logo} label={name}/> : ''}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <AccessDenied/>
                        <AccessDeniedCardContent headingText={"You don’t have the required permission for this service"} primaryText={`Contact an ${isTenancyV4 ? "organization" : "account"} admin to require access.`}/>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
};

let count = 0;

export const NeedSaaSRedirect = React.memo(({ needHeader, currentService }) => {
    const location = useLocation();
    const selectedAccountId = useSelector(state => state.tenancy.selectedAccountId);
    const connectorId = useSelector(state => state.tenancy.selectedAgentId);
    const url = `${externals.saasFrontend}${location.pathname}?accountId=${selectedAccountId}&connectorId=${connectorId}`;
    const { logo, name } = currentService || {};

    useEffect(() => {
        if(count<1){
            window.open(url, "_blank");
            count++;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return <>
        {currentService && needHeader ? <TabHeader logo={logo} label={name}/> : ''}
        <Layout.Container>
            <Layout.Grid centerContent>
                <Layout.GridItem lg={6}>
                    <Card className={styles["card-container"]}>
                        <AccessDenied/>
                        <Heading level={20} style={{marginTop:16, textAlign: "center"}} bold>Service requires a secure connection.</Heading>
                        <Text style={{marginTop:16, marginBottom:16}}>This service is only accessible from BlueXP or local UI served over HTTPS.</Text>
                        <ExternalLink
                            large
                            href={url}
                            newTab
                        > Go to BlueXP</ExternalLink>
                    </Card>
                </Layout.GridItem>
            </Layout.Grid>
        </Layout.Container>
    </>
});
